import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { products } from '../mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Products dropdown: pinned (click-locked) + hovered states
  const [productsPinned, setProductsPinned] = useState(false);
  const [productsHovered, setProductsHovered] = useState(false);
  const [firefightingHovered, setFirefightingHovered] = useState(false);
  const [equipmentHovered, setEquipmentHovered] = useState(false);
  const [fireProtectionHovered, setFireProtectionHovered] = useState(false);

  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileFirefightingOpen, setMobileFirefightingOpen] = useState(false);
  const [mobileEquipmentOpen, setMobileEquipmentOpen] = useState(false);
  const [mobileFireProtectionOpen, setMobileFireProtectionOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const productsRef = useRef(null);

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  const searchRef = useRef(null);

  // Static pages index
  const staticPages = [
    { name: 'Home', description: 'Company overview, featured products and highlights', path: '/' },
    { name: 'About Us', description: 'Company history, mission, vision and team', path: '/about' },
    { name: 'Solutions', description: 'Industry solutions for manufacturing, defence, aviation and more', path: '/solutions' },
    { name: 'MRO', description: 'Maintenance, Repair and Overhaul services', path: '/mro' },
    { name: 'Contact', description: 'Get in touch, send an enquiry or request a quote', path: '/contact' },
    { name: 'News & Media', description: 'Latest news, events and media coverage', path: '/news-media' },
  ];

  const buildProductPath = (product) => {
    const categoryMap = {
      'ARFFV': 'ARFFV',
      'Aerial Vehicles': 'Aerial+Vehicles',
      'Domestic Fire Tenders': 'Domestic+Fire+Tenders',
      'Emergency Rescue Tender': 'Emergency+Rescue+Tender',
      'Forest Firefighting Vehicle': 'Forest+Firefighting+Vehicle',
      'Fire Protection Systems': 'Fire+Protection+Systems',
      'Firefighting Equipment': 'Equipment',
      "Firefighter's Equipment": 'Equipment',
      'Pumps': 'Equipment',
      'Accessories': 'Equipment',
      'Firefighting Robots': 'Firefighting+Robots',
      'Rescue Tools': 'Rescue+Tools',
    };
    const subMap = {
      'Firefighting Equipment': 'Firefighting+Equipment',
      "Firefighter's Equipment": "Firefighter%27s+Equipment",
      'Pumps': 'Pumps',
      'Accessories': 'Accessories',
    };
    const cat = categoryMap[product.category] || product.category.replace(/ /g, '+');
    const sub = subMap[product.category];
    return sub ? `/products?category=${cat}&sub=${sub}` : `/products?category=${cat}`;
  };

  const runSearch = (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    const lq = q.toLowerCase();
    const productHits = products
      .filter(p =>
        p.name.toLowerCase().includes(lq) ||
        (p.description && p.description.toLowerCase().includes(lq)) ||
        p.category.toLowerCase().includes(lq) ||
        (p.features && p.features.some(f => f.toLowerCase().includes(lq)))
      )
      .map(p => {
        // Score for relevance: name match > category match > description/features match
        let score = 0;
        if (p.name.toLowerCase().includes(lq)) score += 3;
        if (p.category.toLowerCase().includes(lq)) score += 2;
        if (p.description && p.description.toLowerCase().includes(lq)) score += 1;
        if (p.features && p.features.some(f => f.toLowerCase().includes(lq))) score += 1;
        return { ...p, _score: score };
      })
      .sort((a, b) => b._score - a._score)
      .slice(0, 6)
      .map(p => ({
        type: 'product',
        _id: p.id,
        name: p.name,
        badge: p.category,
        description: p.description ? p.description.slice(0, 60) + '…' : p.category,
        path: buildProductPath(p),
      }));

    const pageHits = staticPages.filter(pg =>
      pg.name.toLowerCase().includes(lq) ||
      pg.description.toLowerCase().includes(lq)
    ).map(pg => ({ type: 'page', name: pg.name, badge: 'Page', description: pg.description, path: pg.path }));

    setSearchResults([...pageHits, ...productHits].slice(0, 8));
  };

  useEffect(() => { runSearch(searchQuery); }, [searchQuery]);

  // Auto-focus input when search opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [searchOpen]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    if (searchOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchOpen]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setSearchOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close search on route change
  useEffect(() => { setSearchOpen(false); }, [location]);

  // Hover debounce timers
  const productsTimer = useRef(null);
  const firefightingTimer = useRef(null);
  const equipmentTimer = useRef(null);
  const fireProtectionTimer = useRef(null);

  const HOVER_OPEN_DELAY = 180;
  const HOVER_CLOSE_DELAY = 260;

  const isProductsOpen = productsPinned || productsHovered;
  const isFirefightingOpen = firefightingHovered;
  const isEquipmentOpen = equipmentHovered;
  const isFireProtectionOpen = fireProtectionHovered;

  // Helpers for delayed hover open/close
  const scheduleHover = (timerRef, setter, value, delay) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setter(value), delay);
  };

  const openProductsHover = () => scheduleHover(productsTimer, setProductsHovered, true, HOVER_OPEN_DELAY);
  const closeProductsHover = () => {
    scheduleHover(productsTimer, setProductsHovered, false, HOVER_CLOSE_DELAY);
    // Submenus also fade out with the menu
    scheduleHover(firefightingTimer, setFirefightingHovered, false, HOVER_CLOSE_DELAY);
    scheduleHover(equipmentTimer, setEquipmentHovered, false, HOVER_CLOSE_DELAY);
    scheduleHover(fireProtectionTimer, setFireProtectionHovered, false, HOVER_CLOSE_DELAY);
  };

  const openFirefighting = () => {
    scheduleHover(firefightingTimer, setFirefightingHovered, true, HOVER_OPEN_DELAY);
    scheduleHover(equipmentTimer, setEquipmentHovered, false, HOVER_CLOSE_DELAY);
    scheduleHover(fireProtectionTimer, setFireProtectionHovered, false, HOVER_CLOSE_DELAY);
  };
  const closeFirefighting = () => scheduleHover(firefightingTimer, setFirefightingHovered, false, HOVER_CLOSE_DELAY);

  const openEquipment = () => {
    scheduleHover(equipmentTimer, setEquipmentHovered, true, HOVER_OPEN_DELAY);
    scheduleHover(firefightingTimer, setFirefightingHovered, false, HOVER_CLOSE_DELAY);
    scheduleHover(fireProtectionTimer, setFireProtectionHovered, false, HOVER_CLOSE_DELAY);
  };
  const closeEquipment = () => scheduleHover(equipmentTimer, setEquipmentHovered, false, HOVER_CLOSE_DELAY);

  const openFireProtection = () => {
    scheduleHover(fireProtectionTimer, setFireProtectionHovered, true, HOVER_OPEN_DELAY);
    scheduleHover(firefightingTimer, setFirefightingHovered, false, HOVER_CLOSE_DELAY);
    scheduleHover(equipmentTimer, setEquipmentHovered, false, HOVER_CLOSE_DELAY);
  };
  const closeFireProtection = () => scheduleHover(fireProtectionTimer, setFireProtectionHovered, false, HOVER_CLOSE_DELAY);

  const toggleProductsPinned = () => {
    setProductsPinned((prev) => {
      const next = !prev;
      if (next) {
        // When pinning open, make hovered also true immediately so menu shows without delay
        if (productsTimer.current) clearTimeout(productsTimer.current);
        setProductsHovered(true);
      } else {
        // When unpinning, clear hover too so it closes
        if (productsTimer.current) clearTimeout(productsTimer.current);
        setProductsHovered(false);
        setFirefightingHovered(false);
        setEquipmentHovered(false);
        setFireProtectionHovered(false);
      }
      return next;
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside (clears pin + hover)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productsRef.current && !productsRef.current.contains(event.target)) {
        setProductsPinned(false);
        setProductsHovered(false);
        setFirefightingHovered(false);
        setEquipmentHovered(false);
        setFireProtectionHovered(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up timers on unmount
  useEffect(() => {
    const p = productsTimer;
    const ff = firefightingTimer;
    const eq = equipmentTimer;
    const fp = fireProtectionTimer;
    return () => {
      if (p.current) clearTimeout(p.current);
      if (ff.current) clearTimeout(ff.current);
      if (eq.current) clearTimeout(eq.current);
      if (fp.current) clearTimeout(fp.current);
    };
  }, []);

  // Close dropdowns on route change
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setProductsPinned(false);
    setProductsHovered(false);
    setFirefightingHovered(false);
    setEquipmentHovered(false);
    setFireProtectionHovered(false);
    setIsMobileMenuOpen(false);
    setMobileProductsOpen(false);
    setMobileFirefightingOpen(false);
    setMobileEquipmentOpen(false);
    setMobileFireProtectionOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'MRO', path: '/mro' },
    { name: 'Contact', path: '/contact' },
    { name: 'News & Media', path: '/news-media' }
  ];

  const firefightingSubCategories = [
    { name: 'ARFFV', path: '/products?category=ARFFV' },
    { name: 'Aerial Vehicles', path: '/products?category=Aerial+Vehicles' },
    { name: 'DFT', path: '/products?category=Domestic+Fire+Tenders' },
    { name: 'Emergency Rescue Tender', path: '/products?category=Emergency+Rescue+Tender' },
    { name: 'Forest Firefighting Vehicle', path: '/products?category=Forest+Firefighting+Vehicle' }
  ];

  const equipmentSubCategories = [
    { name: 'Firefighting Equipment', path: '/products?category=Equipment&sub=Firefighting+Equipment' },
    { name: "Firefighter's Equipment", path: "/products?category=Equipment&sub=Firefighter%27s+Equipment" },
    { name: 'Pumps', path: '/products?category=Equipment&sub=Pumps' },
    { name: 'Accessories', path: '/products?category=Equipment&sub=Accessories' }
  ];

  const fireProtectionSubCategories = [
    { name: 'Fire Alarm & Detection Systems', path: '/products?category=Fire+Protection+Systems&sub=Fire+Alarm+%26+Detection+Systems' },
    { name: 'Fire Extinguishing System', path: '/products?category=Fire+Protection+Systems&sub=Fire+Extinguishing+System' },
    { name: 'Fire Hydrant System', path: '/products?category=Fire+Protection+Systems&sub=Fire+Hydrant+System' },
    { name: 'Fire Sprinkler System', path: '/products?category=Fire+Protection+Systems&sub=Fire+Sprinkler+System' },
    { name: 'Fire Suppression System', path: '/products?category=Fire+Protection+Systems&sub=Fire+Suppression+System' }
  ];

  const isActiveProducts = location.pathname === '/products';

  return (
    <>
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2 sm:py-2.5 px-4 text-[12px] sm:text-[13px] border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-2">
          <div className="flex items-center gap-3 sm:gap-5 min-w-0">
            <a href="tel:+911202895295" aria-label="Call us" className="flex items-center gap-1.5 hover:text-red-400 transition-colors">
              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="font-medium tracking-wide whitespace-nowrap">+91-120-2895295</span>
            </a>
            <a href="mailto:salesservice@bhartiyavehicles.com" aria-label="Email us" className="flex items-center gap-1.5 hover:text-red-400 transition-colors min-w-0">
              <Mail className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="font-medium tracking-wide hidden sm:inline">salesservice@bhartiyavehicles.com</span>
              <span className="font-medium tracking-wide sm:hidden">Email</span>
            </a>
          </div>
          <div className="text-[11px] sm:text-xs text-slate-300 uppercase tracking-widest font-medium hidden md:block">
            ISO 9001:2015 Certified · 100 Years of Excellence
          </div>
          <div className="text-[10px] text-slate-300 uppercase tracking-wider font-medium md:hidden whitespace-nowrap">
            ISO 9001:2015
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 bg-white border-b ${
          isScrolled ? 'shadow-md border-slate-100' : 'border-slate-100'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-[88px]">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/images/h0a3ygjr_white-logo.png" 
                alt="Bhartiya Vehicles & Engineering Co. Ltd."
                className="h-10 md:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {/* Home */}
              <Link
                to="/"
                className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                  location.pathname === '/'
                    ? 'text-red-600 bg-red-50'
                    : 'text-slate-700 hover:text-red-600 hover:bg-red-50'
                }`}
              >
                Home
              </Link>
              {/* About Us */}
              <Link
                to="/about"
                className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                  location.pathname === '/about'
                    ? 'text-red-600 bg-red-50'
                    : 'text-slate-700 hover:text-red-600 hover:bg-red-50'
                }`}
              >
                About Us
              </Link>

              {/* Products Dropdown */}
              <div
                ref={productsRef}
                className="relative"
                onMouseEnter={openProductsHover}
                onMouseLeave={() => {
                  // Hover cannot close when pinned
                  if (!productsPinned) closeProductsHover();
                }}
              >
                <button
                  onClick={toggleProductsPinned}
                  className={`px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center gap-1 ${
                    isActiveProducts
                      ? 'text-red-600 bg-red-50'
                      : 'text-slate-700 hover:text-red-600 hover:bg-red-50'
                  }`}
                >
                  Products
                  <ChevronDown className={`h-4 w-4 transition-transform ${isProductsOpen ? 'rotate-180' : ''}`} />
                </button>

                {isProductsOpen && (
                  <div className="absolute top-full left-0 pt-2 w-64 z-50" data-testid="products-dropdown">
                    <div className="bg-white rounded-md shadow-xl border border-slate-200 py-2">
                      {/* Firefighting Vehicles with submenu */}
                      <div
                        className="relative"
                        onMouseEnter={openFirefighting}
                        onMouseLeave={closeFirefighting}
                      >
                        <Link
                          to="/products?category=Firefighting+Vehicles"
                          className="w-full flex items-center justify-between px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium"
                        >
                          <span>Firefighting Vehicles</span>
                          <ChevronRight className="h-4 w-4" />
                        </Link>

                        {isFirefightingOpen && (
                          <div className="absolute left-full top-0 pl-2 w-64">
                            <div className="bg-white rounded-md shadow-xl border border-slate-200 py-2">
                              {firefightingSubCategories.map((sub) => (
                                <Link
                                  key={sub.name}
                                  to={sub.path}
                                  className="block px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors text-sm"
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Equipment with submenu */}
                      <div
                        className="relative"
                        onMouseEnter={openEquipment}
                        onMouseLeave={closeEquipment}
                      >
                        <Link
                          to="/products?category=Equipment"
                          className="w-full flex items-center justify-between px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium"
                        >
                          <span>Equipment</span>
                          <ChevronRight className="h-4 w-4" />
                        </Link>

                        {isEquipmentOpen && (
                          <div className="absolute left-full top-0 pl-2 w-64">
                            <div className="bg-white rounded-md shadow-xl border border-slate-200 py-2">
                              {equipmentSubCategories.map((sub) => (
                                <Link
                                  key={sub.name}
                                  to={sub.path}
                                  className="block px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors text-sm"
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Fire Protection Systems with submenu */}
                      <div
                        className="relative"
                        onMouseEnter={openFireProtection}
                        onMouseLeave={closeFireProtection}
                      >
                        <Link
                          to="/products?category=Fire+Protection+Systems"
                          className="w-full flex items-center justify-between px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium"
                        >
                          <span>Fire Protection Systems</span>
                          <ChevronRight className="h-4 w-4" />
                        </Link>

                        {isFireProtectionOpen && (
                          <div className="absolute left-full top-0 pl-2 w-72">
                            <div className="bg-white rounded-md shadow-xl border border-slate-200 py-2">
                              {fireProtectionSubCategories.map((sub) => (
                                <Link
                                  key={sub.name}
                                  to={sub.path}
                                  className="block px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors text-sm"
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Firefighting Robots */}
                      <Link
                        to="/products?category=Firefighting+Robots"
                        className="block px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium"
                      >
                        Firefighting Robots
                      </Link>

                      {/* Rescue Tools */}
                      <Link
                        to="/products?category=Rescue+Tools"
                        className="block px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium"
                      >
                        Rescue Tools
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Other nav links */}
              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                    location.pathname === link.path
                      ? 'text-red-600 bg-red-50'
                      : 'text-slate-700 hover:text-red-600 hover:bg-red-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Search Icon — visible on all screen sizes */}
            <div ref={searchRef} className="relative ml-1">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 rounded-md text-slate-600 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
                aria-label="Search"
                data-testid="search-icon-btn"
              >
                <Search className="h-5 w-5" />
              </button>

              {searchOpen && (
                <div className="fixed sm:absolute left-3 right-3 top-[58px] sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[380px] bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden z-[200]">
                  {/* Input */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                    <Search className="h-4 w-4 text-slate-400 shrink-0" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search products, pages…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 text-sm text-slate-800 placeholder-slate-400 outline-none bg-transparent"
                      data-testid="search-input"
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="text-slate-400 hover:text-slate-600">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Results */}
                  {searchResults.length > 0 ? (
                    <ul className="max-h-80 overflow-y-auto py-1">
                      {searchResults.map((r, i) => (
                        <li key={i}>
                          <Link
                            to={r.path}
                            state={r.type === 'product' ? { scrollToId: `product-${r._id}` } : undefined}
                            className="flex items-start gap-3 px-4 py-3 hover:bg-red-50 group transition-colors"
                            data-testid={`search-result-${i}`}
                          >
                            <Search className="h-3.5 w-3.5 text-slate-300 group-hover:text-red-400 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-sm font-semibold text-slate-800 group-hover:text-red-600 truncate">{r.name}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${r.type === 'page' ? 'bg-slate-100 text-slate-500' : 'bg-red-50 text-red-600'}`}>{r.badge}</span>
                              </div>
                              <p className="text-xs text-slate-500 truncate">{r.description}</p>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : searchQuery.trim() ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">No results found for "{searchQuery}"</div>
                  ) : (
                    <div className="px-4 py-4 text-xs text-slate-400">Start typing to search products and pages…</div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-md text-slate-700 hover:bg-slate-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t">
              <Link
                to="/"
                className={`block px-4 py-3 rounded-md font-medium ${
                  location.pathname === '/'
                    ? 'text-red-600 bg-red-50'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`block px-4 py-3 rounded-md font-medium ${
                  location.pathname === '/about'
                    ? 'text-red-600 bg-red-50'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                About Us
              </Link>

              {/* Mobile Products Dropdown */}
              <div>
                <button
                  onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-md font-medium ${
                    isActiveProducts ? 'text-red-600 bg-red-50' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span>Products</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileProductsOpen && (
                  <div className="pl-4">
                    <div>
                      <button
                        onClick={() => setMobileFirefightingOpen(!mobileFirefightingOpen)}
                        className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
                      >
                        <span>Firefighting Vehicles</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${mobileFirefightingOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileFirefightingOpen && (
                        <div className="pl-4">
                          {firefightingSubCategories.map((sub) => (
                            <Link
                              key={sub.name}
                              to={sub.path}
                              className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <button
                        onClick={() => setMobileEquipmentOpen(!mobileEquipmentOpen)}
                        className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
                      >
                        <span>Equipment</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${mobileEquipmentOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileEquipmentOpen && (
                        <div className="pl-4">
                          {equipmentSubCategories.map((sub) => (
                            <Link
                              key={sub.name}
                              to={sub.path}
                              className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <button
                        onClick={() => setMobileFireProtectionOpen(!mobileFireProtectionOpen)}
                        className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
                      >
                        <span>Fire Protection Systems</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${mobileFireProtectionOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileFireProtectionOpen && (
                        <div className="pl-4">
                          {fireProtectionSubCategories.map((sub) => (
                            <Link
                              key={sub.name}
                              to={sub.path}
                              className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                    <Link
                      to="/products?category=Firefighting+Robots"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
                    >
                      Firefighting Robots
                    </Link>
                    <Link
                      to="/products?category=Rescue+Tools"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
                    >
                      Rescue Tools
                    </Link>
                  </div>
                )}
              </div>

              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-md font-medium ${
                    location.pathname === link.path
                      ? 'text-red-600 bg-red-50'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          )}
        </nav>
      </header>
    </>
  );
};

export default Header;
